import React from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { Link } from "react-router-dom";
import { Trans } from "react-i18next";
import { ReactComponent as InvalidDocument } from "@/assets/images/invalid_document.svg";

import Button from "@/components/atoms/Buttons/Button";
import Input from "@/components/atoms/Input";
import Switch from "@/components/atoms/Switch";
import { FakeInput } from "@/components/molecules/Wrappers/FakeInput";
import { AmountInput } from "@/components/molecules/Wrappers/AmountInput";
import { conditionalUserSchema } from "@/utils/validations";
import { formatDocumentMask } from "@/utils";
import i18n from "@/translate/i18n";

import { useUserDataForm } from "./useUserDataForm";

function UserDataForm1xbet({
  data,
  setData,
  setError,
  setIsFetching,
  retryableError,
  setRetryableError,
}) {
  const {
    documentInstructions,
    emailHasErrors,
    isValidDocument,
    isValidEmail,
    acceptedTerms,
    setAcceptedTerms,
    setIsValidEmail,
    initialAcceptedTerms,
    initialCountryIso,
    initialDocumentType,
    initialDocumentNumber,
    initialEmail,
    isEmailMissingInDeposit,
    incompleteData,
    requestTransaction,
    requestUserData,
    handleDocumentFocus,
    handleDocumentBlur,
    handleEmailFocus,
    handleEmailBlur,
    handleDocumentChange,
    validEmail,
    history,
    id,
    token,
  } = useUserDataForm({
    data,
    setData,
    setError,
    setIsFetching,
    setRetryableError,
  });

  return (
    <Formik
      validationSchema={conditionalUserSchema}
      initialValues={{
        accepted_terms: acceptedTerms,
        merchant_request_amount: data?.merchant_request_amount || "",
        bank_payment_data: data?.bank_payment_data || "",
        document_number: initialDocumentNumber,
        document_type: initialDocumentType,
        country_iso: initialCountryIso,
        email: initialEmail,
      }}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={incompleteData ? requestUserData : requestTransaction}
      context={{ incompleteData }}
    >
      {({
        errors,
        setErrors,
        setFieldTouched,
        setFieldError,
        setFieldValue,
        values,
      }) =>
        retryableError ? (
          <div className="size-full flex grow flex-col justify-around items-center">
            <InvalidDocument />
            <div className="flex flex-col gap-6 px-6">
              <p className="text-xl font-bold">{retryableError.title}</p>
              <p className="text-lg font-bold">{retryableError.message}</p>
              <p className="text-xs">{retryableError.action}</p>
            </div>
            <Button type="button" handleClick={() => setRetryableError(null)}>
              {i18n.t("error.tryAgainButton") || "Volver a intentar"}
            </Button>
          </div>
        ) : (
          <Form onChange={() => setErrors({})} className="w-full">
            {data?.merchant_request_amount && (
              <AmountInput
                value={data?.merchant_request_amount}
                currency={String(data?.merchant_request_currency_iso || "")}
              />
            )}
            <div className="mb-5 mx-auto text-black text-lg max-w-xs font-semibold">
              {i18n.t([`checkout.${data?.type}.formTitle`, ""])}
            </div>
            <div className="flex flex-col justify-between items-center">
              {!data?.merchant_request_amount && (
                <Input
                  id="merchant_request_amount"
                  type="text"
                  label={i18n.t("form.amountLabel.default")}
                  placeholder={i18n.t("form.amountPlaceholder")}
                  value={values?.merchant_request_amount || ""}
                  error={!!errors?.merchant_request_amount}
                  disabled={!!values?.merchant_request_amount}
                  errorText={errors?.merchant_request_amount}
                />
              )}
              {initialDocumentNumber && data?.type === "deposit" ? (
                <FakeInput
                  label={i18n.t(
                    `form.document.${values?.document_type || "generic"}`,
                  )}
                  value={formatDocumentMask(
                    values?.document_number,
                    values?.document_type,
                  )}
                />
              ) : (
                <div className="w-full mb-5">
                  <Input
                    id="document_number"
                    name="document_number"
                    type="text"
                    label={i18n.t(
                      `form.document.${values?.document_type || "generic"}`,
                    )}
                    placeholder={i18n.t("form.documentPlaceholder")}
                    value={values?.document_number || ""}
                    error={!!errors?.document_number}
                    handleFocus={() => handleDocumentFocus(setFieldError)}
                    handleBlur={() =>
                      handleDocumentBlur(
                        values?.document_number,
                        setFieldTouched,
                        setFieldError,
                      )
                    }
                    handleChange={({ target }) =>
                      handleDocumentChange(
                        target,
                        values,
                        setFieldTouched,
                        setFieldValue,
                      )
                    }
                    maxLength={13}
                    disabled={!!initialDocumentNumber}
                  />
                  {documentInstructions.show && (
                    <div
                      className={`mt-1 text-[0.65rem] pl-1 text-left ${
                        documentInstructions.type === "error"
                          ? "text-red-500"
                          : "text-gray-500"
                      }`}
                    >
                      {documentInstructions.message}
                    </div>
                  )}
                </div>
              )}
              {isEmailMissingInDeposit ? (
                <div className="w-full mb-5">
                  <Input
                    id="email"
                    name="email"
                    type="text"
                    label={i18n.t("form.email")}
                    placeholder={i18n.t("form.emailPlaceholder")}
                    value={values?.email || ""}
                    error={!!errors?.email}
                    handleFocus={() => handleEmailFocus(setFieldError)}
                    handleChange={({ target }) => {
                      setFieldValue("email", target.value);
                      setIsValidEmail(validEmail(target.value));
                    }}
                    handleBlur={() =>
                      handleEmailBlur(
                        values?.email,
                        setFieldTouched,
                        setFieldError,
                      )
                    }
                    disabled={Boolean(data?.customer?.email)}
                  />
                  {emailHasErrors && (
                    <div className="mt-1 text-[0.65rem] text-red-500 pl-1 text-left">
                      {i18n.t("form.invalidEmail")}
                    </div>
                  )}
                </div>
              ) : (
                <FakeInput label={i18n.t("form.email")} value={values?.email} />
              )}
            </div>
            {!initialAcceptedTerms && (
              <div
                className="flex mt-5 items-center gap-3 bg-white"
                data-theme="systemSwitchTheme"
              >
                <Switch
                  isChecked={values?.accepted_terms}
                  onChange={(e) => {
                    setFieldTouched("accepted_terms");
                    setFieldValue("accepted_terms", !values.accepted_terms);
                    setAcceptedTerms(e.target.checked);
                  }}
                />
                <p className="m-0 text-left text-sm text-black">
                  <Trans
                    i18nKey="form.terms"
                    i18n={i18n}
                    components={[
                      <Link
                        className="text-blue-600 hover:text-blue-800 font-bold underline"
                        to={{
                          pathname: `/terms-and-conditions/${id}/${token}`,
                          search: history.location.search,
                        }}
                      />,
                    ]}
                  />
                </p>
                {errors?.accepted_terms && (
                  <div className="text-red-500 text-xs absolute">
                    {errors.accepted_terms}
                  </div>
                )}
              </div>
            )}
            <Button
              type="submit"
              disabled={!acceptedTerms || !isValidDocument || !isValidEmail}
            >
              {!incompleteData
                ? i18n.t([`form.submit.${data?.type}`, "form.submit.default"])
                : i18n.t("form.submit.next")}
            </Button>
          </Form>
        )
      }
    </Formik>
  );
}

UserDataForm1xbet.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.number,
    merchant_request_amount: PropTypes.number,
    customer_country_iso: PropTypes.string,
    customer_document_types: PropTypes.arrayOf(PropTypes.string),
    document_number: PropTypes.string,
    bank_payment_data: PropTypes.shape({
      qr_code: PropTypes.string,
      account_holder: PropTypes.string,
      account_id: PropTypes.string,
      bank_name: PropTypes.string,
    }),
    merchant_request_currency_iso: PropTypes.string,
    redirect_url_failed: PropTypes.string,
    type: PropTypes.string,
    customer: PropTypes.shape({
      birth_date: PropTypes.string,
      document_number: PropTypes.string,
      document_type: PropTypes.string,
      country_iso: PropTypes.string,
      email: PropTypes.string,
      name: PropTypes.string,
      terms_accepted_at: PropTypes.string,
    }),
    customer_data: PropTypes.shape({
      birth_date: PropTypes.string,
      document_number: PropTypes.string,
      email: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  setData: PropTypes.func,
  setError: PropTypes.func,
  setIsFetching: PropTypes.func,
  retryableError: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
    action: PropTypes.string,
  }),
  setRetryableError: PropTypes.func,
};

export default UserDataForm1xbet;
